import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ClipboardList, CheckSquare } from "lucide-react";
import TopBar from "../components/TopBar";
import './css/TakeTest.css';
import axios from "./../axios";
import toast from "react-hot-toast";

const TakeTest = ({ setSideBar, setSettingDrawer }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [tests, setTests] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("student_token");

        const [testttt] = await Promise.all([
          axios.get('student/mcq_start/' + id, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setTests(testttt.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const test = tests[id];

  if (isLoading) {
    return (
      <>
        <TopBar title="Take MCQ Test" setSideBar={setSideBar} setSettingDrawer={setSettingDrawer} />
        <div className="take-test-container animate-pulse">
          <div className="take-test-header flex items-center gap-2">
            <div className="w-5 h-5 bg-gray-300 rounded"></div>
            <div className="h-6 bg-gray-300 rounded w-1/3"></div>
          </div>
          <div className="details-card">
            <div className="question-section">
              <div className="h-5 bg-gray-300 rounded w-1/4 mb-4"></div>
              <div className="h-6 bg-gray-300 rounded w-3/4 mb-4"></div>
              <div className="space-y-3">
                {[...Array(4)].map((_, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-300 rounded-full"></div>
                    <div className="h-5 bg-gray-300 rounded w-2/3"></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="navigation-group flex gap-2">
              <div className="h-10 bg-gray-300 rounded w-24"></div>
              <div className="h-10 bg-gray-300 rounded w-24"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!test) {
    console.error(`Test with id ${id} not found`);
    return (
      <>
        <TopBar title="Take MCQ Test" setSideBar={setSideBar} setSettingDrawer={setSettingDrawer} />
        <div className="take-test-container">
          <div className="take-test-header">
            <ClipboardList size={20} className="header-icon" />
            <h2>Test Not Found</h2>
          </div>
          <div className="details-card">
            <div className="no-test-message">
              Sorry, the requested test could not be found. Please check the test ID or return to the tests list.
            </div>
          </div>
        </div>
      </>
    );
  }

  const currentQuestion = test.questions[currentQuestionIndex];

  const handleAnswerSelect = (questionId, optionId) => {
    setAnswers({ ...answers, [questionId]: optionId });
  };

  const handleNext = () => {
    if (currentQuestionIndex < test.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const token = localStorage.getItem("student_token");
      await axios.post(
        `/student/mcq-test/${id}/submit`,
        { answers },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Response Successfuly Submitted')
      navigate(`/student/mcq-test/${test.id}/response`);
    } catch (error) {
      console.error('Error submitting test:', error);
      setSubmitError('Failed to submit test. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <TopBar title="Take MCQ Test" setSideBar={setSideBar} setSettingDrawer={setSettingDrawer} />
      <div className="take-test-container">
        <div className="take-test-header">
          <ClipboardList size={20} className="header-icon" />
          <h2>{test.title}</h2>
        </div>
        <div className="details-card">
          <div className="question-section">
            <h3 className="question-header">
              Question {currentQuestionIndex + 1} of {test.questions.length}
            </h3>
            <p className="question-text">{currentQuestion.text}</p>
            {currentQuestion.image && (
              <img src={currentQuestion.image} alt="Question" className="question-image" />
            )}
            <div className="options-list">
              {currentQuestion.options.map((option) => (
                <label key={option.id} className="option-item">
                  <input
                    type="radio"
                    name={`question-${currentQuestion.id}`}
                    value={option.id}
                    checked={answers[currentQuestion.id] === option.id}
                    onChange={() => handleAnswerSelect(currentQuestion.id, option.id)}
                    disabled={isSubmitting}
                  />
                  <span className="option-content">
                    {option.image && (
                      <img src={option.image} alt="Option" className="option-image" />
                    )}
                    {option.content}
                  </span>
                </label>
              ))}
            </div>
          </div>
          {submitError && (
            <div className="text-red-500 text-center mb-4">{submitError}</div>
          )}
          <div className="navigation-group">
            <button
              className="nav-button"
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0 || isSubmitting}
            >
              Previous
            </button>
            {currentQuestionIndex < test.questions.length - 1 ? (
              <button
                className="nav-button"
                onClick={handleNext}
                disabled={isSubmitting}
              >
                Next
              </button>
            ) : (
              <button
                className="submit-button"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                <CheckSquare size={16} />
                {isSubmitting ? 'Submitting...' : 'Submit Test'}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TakeTest;